import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { usePrevious } from "./user/ui/utils";
import { AppState } from "./RootReducer";
import { LoginStatus } from "./http/Types";
import { getContainer } from "./ioc/IOCSetup";
import { addScript } from "./bootstrap-utils";

enum WidgetLoadStatus {
     INITIAL,
     LOADING_WIDGETS,
     SHOULD_DO_WIDGET_LOGIN,
     FINISHED,
}

const Widgets = () => {
     const settings = useSelector((app: AppState) => app.settings.settings);
     const status = useSelector((app: AppState) => app.user.status);
     const prevStatus = usePrevious(status);
     const location = useLocation();
     const widgets = useMemo(() => settings?.widgets ?? [], [settings]);
     const [widgetLoadStatus, setWidgetLoadStatus] = useState(WidgetLoadStatus.INITIAL);
     const statusRef = useRef(status);

     useEffect(() => {
          statusRef.current = status;
     }, [status]);

     useEffect(() => {
          const loadScripts = async () => {
               if (widgets?.length < 1) return;

               setWidgetLoadStatus(WidgetLoadStatus.LOADING_WIDGETS);
               await Promise.all(
                    widgets.map(({ scriptUrl }) => {
                         if (scriptUrl) {
                              return addScript(scriptUrl);
                         }
                         return Promise.resolve();
                    })
               );

               // statusRef must be used here, as we need the current status that is set when the widgets has been loaded and not the status that was set when the this effect was created
               if (statusRef.current === LoginStatus.LOGGED_IN) {
                    setWidgetLoadStatus(WidgetLoadStatus.SHOULD_DO_WIDGET_LOGIN);
               } else {
                    setWidgetLoadStatus(WidgetLoadStatus.FINISHED);
               }
          };

          loadScripts();
     }, [widgets]);

     useEffect(() => {
          if (widgetLoadStatus !== WidgetLoadStatus.FINISHED || widgets.length < 1) return;
          widgets.forEach((w) => {
               if (w.variableName) {
                    try {
                         (window as any)[w.variableName].historyChange?.(location.pathname);
                    } catch (e) {
                         console.error(`Widget not found: ${w.variableName}\n`, e);
                    }
               }
          });
     }, [location.pathname, widgets, widgetLoadStatus]);

     useEffect(() => {
          if (widgetLoadStatus === WidgetLoadStatus.INITIAL || widgetLoadStatus === WidgetLoadStatus.LOADING_WIDGETS || widgets.length < 1) return;

          const doLogin = async () => {
               const widgetsToAuthenticate = await getContainer().getUserService().widgetsAuthenticate();
               widgetsToAuthenticate.forEach((w) => {
                    if (!w.variableName || !w.authenticationRequired) return;

                    try {
                         if (!w.error) {
                              (window as any)[w.variableName].login?.(w.token);
                         } else {
                              (window as any)[w.variableName].error?.();
                         }
                    } catch (e) {
                         console.error(`Widget not found: ${w.variableName}\n`, e);
                    }
               });
               if (widgetLoadStatus !== WidgetLoadStatus.FINISHED) setWidgetLoadStatus(WidgetLoadStatus.FINISHED);
          };

          if (widgetLoadStatus === WidgetLoadStatus.SHOULD_DO_WIDGET_LOGIN) {
               doLogin();
               return;
          }

          const userRecentlyLoggedIn = status === LoginStatus.LOGGED_IN && prevStatus !== LoginStatus.LOGGED_IN;
          const userRecentlyLoggedOut = status === LoginStatus.LOGGED_OUT && prevStatus !== LoginStatus.LOGGED_OUT;

          if (userRecentlyLoggedIn) {
               doLogin();
          }
          if (userRecentlyLoggedOut) {
               widgets.forEach((w) => {
                    if (w.variableName) {
                         try {
                              (window as any)[w.variableName].logout();
                         } catch (e) {
                              console.error(`Widget not found: ${w.variableName}\n`, e);
                         }
                    }
               });
          }
     }, [status, widgets, prevStatus, widgetLoadStatus]);

     return <></>;
};

export default Widgets;
